import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, InputGroup, Modal } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import { BsPencilSquare, BsTrash, BsSearch, BsSortDown, BsSortUp } from 'react-icons/bs';
import { API_ENDPOINTS } from '../config';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [imageFile, setImageFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    mrp: '',
    sellingPrice: '',
    lotSize: '',
    manufacturerName: '',
    discount: '',
    image: null,
    category: 'pharma' // Default to 'pharma'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const response = await axios.get(API_ENDPOINTS.getAllProducts, {
        headers: { 
          'token': token,
          'userId': userId 
        }
      });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const sortProducts = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredProducts = products
    .filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedAndFilteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deleteProduct, {
          headers: {
            'token': token,
            'productId': productId
          }
        });
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct({ ...product });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleEditSubmit = async () => {
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append('image', imageFile);
      }
      formData.append('productId', editingProduct.productId);
      formData.append('name', editingProduct.name);
      formData.append('description', editingProduct.description);
      formData.append('price', editingProduct.price);
      formData.append('mrp', editingProduct.mrp);
      formData.append('sellingPrice', editingProduct.sellingPrice);
      formData.append('lotSize', editingProduct.lotSize);
      formData.append('manufacturerName', editingProduct.manufacturerName);
      formData.append('discount', editingProduct.discount);
      formData.append('category', editingProduct.category); // Include category

      const token = localStorage.getItem('token');
      await axios.patch(API_ENDPOINTS.editProduct, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'token': token,
          'productId': editingProduct.productId
        }
      });
      setEditingProduct(null);
      setImageFile(null);
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleModalSubmit = async () => {
    try {
      const formData = new FormData();
      if (newProduct.image) {
        formData.append('image', newProduct.image);
      }
      formData.append('name', newProduct.name);
      formData.append('description', newProduct.description);
      formData.append('price', newProduct.price);
      formData.append('mrp', newProduct.mrp);
      formData.append('sellingPrice', newProduct.sellingPrice);
      formData.append('lotSize', newProduct.lotSize);
      formData.append('manufacturerName', newProduct.manufacturerName);
      formData.append('discount', newProduct.discount);
      formData.append('category', newProduct.category); // Include category

      const token = localStorage.getItem('token');
      await axios.post(API_ENDPOINTS.createProduct, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'token': token
        }
      });
      setShowModal(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        mrp: '',
        sellingPrice: '',
        lotSize: '',
        manufacturerName: '',
        discount: '',
        image: null,
        category: 'pharma' // Reset to default category
      });
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({
      ...newProduct,
      [name]: value
    });
  };

  const handleModalImageChange = (e) => {
    setNewProduct({
      ...newProduct,
      image: e.target.files[0]
    });
  };

  return (
    <div className="d-flex">
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{  width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid className="mt-3">
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Products</h2>
                <Button variant="primary" onClick={() => setShowModal(true)}>Add Product</Button>
              </div>
              <InputGroup className="mb-3">
                <InputGroup.Text><BsSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th onClick={() => sortProducts('name')}>
                      Name {sortField === 'name' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('description')}>
                      Description {sortField === 'description' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('price')}>
                      Price {sortField === 'price' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('mrp')}>
                      MRP {sortField === 'mrp' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('sellingPrice')}>
                      Selling Price {sortField === 'sellingPrice' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('lotSize')}>
                      Lot Size {sortField === 'lotSize' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('manufacturerName')}>
                      Manufacturer Name {sortField === 'manufacturerName' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('discount')}>
                      Discount {sortField === 'discount' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortProducts('category')}>
                      Category {sortField === 'category' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((product) => (
                    <tr key={product.productId}>
                      <td>{product.name}</td>
                      <td>{product.description}</td>
                      <td>{product.price}</td>
                      <td>{product.mrp}</td>
                      <td>{product.sellingPrice}</td>
                      <td>{product.lotSize}</td>
                      <td>{product.manufacturerName}</td>
                      <td>{product.discount}</td>
                      <td>{product.category}</td>
                      <td>
                        <Button variant="warning" onClick={() => handleEditProduct(product)}><BsPencilSquare /></Button>
                        <Button variant="danger" onClick={() => handleDeleteProduct(product.productId)}><BsTrash /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                {[...Array(Math.ceil(sortedAndFilteredProducts.length / productsPerPage))].map((_, index) => (
                  <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => paginate(index + 1)}>
                    {index + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </Col>
          </Row>
        </Container>

        {/* Add/Edit Product Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingProduct ? 'Edit Product' : 'Add Product'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="formProductName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter product name"
                  name="name"
                  value={editingProduct ? editingProduct.name : newProduct.name}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, name: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductDescription">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter product description"
                  name="description"
                  value={editingProduct ? editingProduct.description : newProduct.description}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, description: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductPrice">
                <Form.Label>Price</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter product price"
                  name="price"
                  value={editingProduct ? editingProduct.price : newProduct.price}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, price: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductMrp">
                <Form.Label>MRP</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter product MRP"
                  name="mrp"
                  value={editingProduct ? editingProduct.mrp : newProduct.mrp}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, mrp: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductSellingPrice">
                <Form.Label>Selling Price</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter product selling price"
                  name="sellingPrice"
                  value={editingProduct ? editingProduct.sellingPrice : newProduct.sellingPrice}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, sellingPrice: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductLotSize">
                <Form.Label>Lot Size</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter lot size"
                  name="lotSize"
                  value={editingProduct ? editingProduct.lotSize : newProduct.lotSize}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, lotSize: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductManufacturerName">
                <Form.Label>Manufacturer Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter manufacturer name"
                  name="manufacturerName"
                  value={editingProduct ? editingProduct.manufacturerName : newProduct.manufacturerName}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, manufacturerName: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductDiscount">
                <Form.Label>Discount</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter discount"
                  name="discount"
                  value={editingProduct ? editingProduct.discount : newProduct.discount}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, discount: e.target.value }) : handleModalChange}
                />
              </Form.Group>
              <Form.Group controlId="formProductCategory">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  as="select"
                  name="category"
                  value={editingProduct ? editingProduct.category : newProduct.category}
                  onChange={editingProduct ? (e) => setEditingProduct({ ...editingProduct, category: e.target.value }) : handleModalChange}
                >
                  <option value="pharma">Pharma</option>
                  <option value="selfcare">Selfcare</option>
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="formProductImage">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={editingProduct ? handleImageChange : handleModalImageChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={editingProduct ? handleEditSubmit : handleModalSubmit}>
              {editingProduct ? 'Save Changes' : 'Add Product'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Products;
